import { jsPDF } from 'jspdf';
import signatureImage from '../assets/invoice-signature.jpg';

const ZURICH_TIMEZONE = 'Europe/Zurich';

const COMPANY = {
  name: 'Financial Market Navigator AG',
  shortName: 'Financial Market Navigator AG',
  street: 'Brandisstrasse 12a',
  zip: '7000',
  city: 'Chur',
  phone: '+41 77 498 16 26',
  email: 'info@findyourbroker.ai',
  uid: 'CHE-237.993.656 MWST',
  bankCHF: {
    holder: 'Financial Market Navigator AG',
    iban: 'CH13 0077 4010 5373 7610 0',
    bic: 'GRKBCH2270A',
  },
  bankEUR: {
    holder: 'Financial Market Navigator AG',
    iban: 'BE44 9052 1434 4745',
    bic: 'TRWIBEB1XXX',
  },
  bankUSD: {
    holder: 'Financial Market Navigator AG',
    account: '936600222676445',
    routing: '084009519',
    bic: 'TRWIUS35XXX',
  },
};

const TEMPLATES = {
  CHF: {
    locale: 'de-CH',
    subject: 'Rechnung Nr.',
    greeting: 'Sehr geehrte Damen und Herren',
    introA: 'Merci vielmal für den Auftrag zum Aufzeigen des gesamten Marktes basierend auf Ihrem Verhalten.',
    introB: 'Für die heute erbrachte Leistung erlaube ich mir folgende Rechnung zu stellen:',
    product: 'Produkt',
    date: 'Datum',
    price: 'Preis',
    count: 'Anzahl',
    net: 'Gesamtpreis exklusive MwSt.',
    vat: 'MwSt. zu 8.1%',
    total: 'Gesamtpreis inklusive MwSt.',
    closing: 'Sonnige Grüsse',
    terms: (deadline) =>
      `Zahlungsfrist von 30 Tagen endet am ${deadline}. Sofern die Zahlung nicht bereits vor der Ansicht Ihrer Ergebnisse vollzogen wurde, bitte den Geldeingang bis zum genannten Datum leisten.`,
  },
  EUR: {
    locale: 'en-GB',
    subject: 'Invoice Nr.',
    greeting: 'Hello together',
    introA: 'Thank you for your request to present the whole market based on your behavior.',
    introB: 'For the today delivered result I allow my self to present you this bill:',
    product: 'Product',
    date: 'Date',
    price: 'Price',
    count: 'Count',
    net: 'Total Price exclusive VAT',
    vat: 'VAT zu 0.0%',
    total: 'Total Price inclusive VAT',
    closing: 'Best regards',
    terms: (deadline) =>
      `The 30-day payment period ends on ${deadline}. If payment has not already been made before viewing your results, please ensure that payment is received by the specified date.`,
  },
  USD: {
    locale: 'en-GB',
    subject: 'Invoice Nr.',
    greeting: 'Hello together',
    introA: 'Thank you for your request to present the whole market based on your behavior.',
    introB: 'For the today delivered result I allow my self to present you this bill:',
    product: 'Product',
    date: 'Date',
    price: 'Price',
    count: 'Count',
    net: 'Total Price exclusive VAT',
    vat: 'VAT zu 0.0%',
    total: 'Total Price inclusive VAT',
    closing: 'Best regards',
    terms: (deadline) =>
      `The 30-day payment period ends on ${deadline}. If payment has not already been made before viewing your results, please ensure that payment is received by the specified date.`,
  },
};

const COUNTRY_MAP = {
  de: { CH: 'Schweiz', DE: 'Deutschland', AT: 'Österreich', FR: 'Frankreich', IT: 'Italien' },
  en: { CH: 'Switzerland', DE: 'Germany', AT: 'Austria', FR: 'France', IT: 'Italy' },
};

const PRODUCT_NAMES = {
  standard: { en: 'Standard', de: 'Standard' },
  premium: { en: 'Premium', de: 'Premium' },
};

function parseDate(dateStr) {
  if (dateStr instanceof Date) {
    return Number.isNaN(dateStr.getTime()) ? new Date() : dateStr;
  }
  if (!dateStr) return new Date();
  const raw = String(dateStr).trim();
  const normalized = raw.replace(' ', 'T');
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
  const iso = hasTimezone ? normalized : `${normalized}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function getLastSundayUtcDay(year, monthZeroBased) {
  const lastDay = new Date(Date.UTC(year, monthZeroBased + 1, 0));
  const dayOfWeek = lastDay.getUTCDay(); // 0 = Sunday
  return lastDay.getUTCDate() - dayOfWeek;
}

function getZurichOffsetHours(utcDate) {
  // CET/CEST transition rules (Europe): starts last Sunday of March at 01:00 UTC,
  // ends last Sunday of October at 01:00 UTC.
  const year = utcDate.getUTCFullYear();
  const dstStartUtc = Date.UTC(year, 2, getLastSundayUtcDay(year, 2), 1, 0, 0);
  const dstEndUtc = Date.UTC(year, 9, getLastSundayUtcDay(year, 9), 1, 0, 0);
  const ts = utcDate.getTime();
  return (ts >= dstStartUtc && ts < dstEndUtc) ? 2 : 1;
}

function getZurichParts(dateStr) {
  const utcDate = parseDate(dateStr);
  const offsetHours = getZurichOffsetHours(utcDate);
  const shifted = new Date(utcDate.getTime() + offsetHours * 60 * 60 * 1000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
  };
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatDateTime(dateStr, locale) {
  const p = getZurichParts(dateStr);
  if (locale === 'de-CH') {
    return `${pad2(p.day)}.${pad2(p.month)}.${p.year}, ${pad2(p.hour)}:${pad2(p.minute)}`;
  }
  return `${pad2(p.day)}/${pad2(p.month)}/${p.year}, ${pad2(p.hour)}:${pad2(p.minute)}`;
}

function formatLongDate(dateStr, locale) {
  const p = getZurichParts(dateStr);
  if (locale === 'de-CH') {
    const monthsDe = ['Januar', 'Februar', 'Maerz', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    return `${p.day}. ${monthsDe[p.month - 1]} ${p.year}`;
  }
  const monthsEn = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${pad2(p.day)} ${monthsEn[p.month - 1]} ${p.year}`;
}

function getInvoiceNumber(orderId, orderDate) {
  const year = getZurichParts(orderDate).year;
  return `B${year}100${String(orderId || 1).padStart(3, '0')}`;
}

function getProductName(product, language) {
  const p = PRODUCT_NAMES[product] || PRODUCT_NAMES.standard;
  return language === 'de' ? p.de : p.en;
}

function getClientCountryName(countryCode, language) {
  const table = language === 'de' ? COUNTRY_MAP.de : COUNTRY_MAP.en;
  return table[countryCode] || countryCode || '';
}

function getBankFooterLines(currency) {
  if (currency === 'EUR') {
    return [
      `EUR-Wise: ${COMPANY.bankEUR.holder}, IBAN: ${COMPANY.bankEUR.iban}, BIC: ${COMPANY.bankEUR.bic}`,
      `CHF-GKB: ${COMPANY.bankCHF.holder}, IBAN: ${COMPANY.bankCHF.iban}, BIC: ${COMPANY.bankCHF.bic}`,
    ];
  }
  if (currency === 'USD') {
    return [
      `USD-Wise: ${COMPANY.bankUSD.holder}, Acct: ${COMPANY.bankUSD.account}, Routing: ${COMPANY.bankUSD.routing}, BIC: ${COMPANY.bankUSD.bic}`,
      `CHF-GKB: ${COMPANY.bankCHF.holder}, IBAN: ${COMPANY.bankCHF.iban}, BIC: ${COMPANY.bankCHF.bic}`,
    ];
  }
  return [
    `CHF-GKB: ${COMPANY.bankCHF.holder}, IBAN: ${COMPANY.bankCHF.iban}, BIC: ${COMPANY.bankCHF.bic}`,
    `EUR-Wise: ${COMPANY.bankEUR.holder}, IBAN: ${COMPANY.bankEUR.iban}, BIC: ${COMPANY.bankEUR.bic}`,
  ];
}

function fmtAmount(n, currency) {
  return `${parseFloat(n || 0).toFixed(2)} ${currency}`;
}

function drawKeyValueTable(doc, rows, startY) {
  const left = 20;
  const middle = 84;
  const right = 190;
  const rowH = 6;
  let y = startY;

  doc.setLineWidth(0.2);
  doc.setDrawColor(0);
  doc.setFontSize(10);

  rows.forEach((row) => {
    doc.line(left, y, right, y);
    doc.text(`${row.label}:`, left + 2, y + 4.2);
    doc.text(`${row.value}`, middle + 2, y + 4.2);
    y += rowH;
  });
  doc.line(left, y, right, y);
  doc.line(middle, startY, middle, y);
  return y + 6;
}

function loadImageAsDataURL(url) {
  return new Promise((resolve) => {
    fetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => resolve(null);
        fr.readAsDataURL(blob);
      })
      .catch(() => resolve(null));
  });
}

function drawCenteredFooter(doc, pageWidth, footerY, footerHeight, title, lines, footerFontSize = 7) {
  doc.setFillColor(126, 159, 209);
  doc.rect(0, footerY, pageWidth, footerHeight, 'F');
  doc.setTextColor(255);
  doc.setFontSize(footerFontSize);

  const allLines = [title, ...lines];
  const maxLineWidth = allLines.reduce((max, line) => {
    const w = doc.getTextWidth(line);
    return w > max ? w : max;
  }, 0);
  const startX = (pageWidth - maxLineWidth) / 2;

  let y = footerY + 4;
  doc.text(title, startX, y);
  y += 4;

  lines.forEach((line) => {
    doc.text(line, startX, y);
    y += 4;
  });
}

export default async function generateInvoicePDF(order) {
  const currency = String(order.currency || 'CHF').toUpperCase();
  const tpl = TEMPLATES[currency] || TEMPLATES.CHF;
  const language = tpl.locale === 'de-CH' ? 'de' : 'en';
  const vatRate = currency === 'CHF' ? 0.081 : 0;
  const orderDate = order.date || new Date().toISOString();
  const invoiceNum = getInvoiceNumber(order.id, orderDate);

  const totalIncVat = parseFloat(order.amount) || 100;
  const netAmount = parseFloat((vatRate > 0 ? totalIncVat / (1 + vatRate) : totalIncVat).toFixed(2));
  const vatAmount = parseFloat((totalIncVat - netAmount).toFixed(2));

  const client = order.clientDetails || {};
  const clientName = client.companyName || [client.firstName, client.lastName].filter(Boolean).join(' ') || (language === 'de' ? 'Kunde' : 'Client');
  const clientStreet = [client.street, client.houseNumber].filter(Boolean).join(' ');
  const clientCity = [client.zipCode, client.town].filter(Boolean).join(' ');
  const clientCountry = getClientCountryName(client.country, language);
  const productName = getProductName(order.product, language);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const left = 16;
  const right = pageWidth - 16;

  // Right-side company block
  let y = 20;
  doc.setFontSize(11);
  doc.text(COMPANY.name, 116, y); y += 7;
  doc.text(COMPANY.street, 116, y); y += 5;
  doc.text(`${COMPANY.zip} ${COMPANY.city}`, 116, y); y += 7;
  doc.text(`Mobil: ${COMPANY.phone}`, 116, y); y += 5;
  doc.text(`E-Mail: ${COMPANY.email}`, 116, y); y += 7;
  doc.text(`UID: ${COMPANY.uid}`, 116, y);

  // Sender line (small)
  doc.setTextColor(100);
  doc.setFontSize(6.5);
  doc.text(`${COMPANY.shortName}, ${COMPANY.street}, ${COMPANY.zip} ${COMPANY.city}`, left, 40);
  doc.setDrawColor(100);
  doc.line(left, 41.5, 95, 41.5);
  doc.setTextColor(0);

  // Recipient block
  let ry = 52;
  doc.setFontSize(12);
  doc.text(clientName, left, ry); ry += 6;
  if (clientStreet) { doc.text(clientStreet, left, ry); ry += 6; }
  if (clientCity) { doc.text(clientCity, left, ry); ry += 6; }
  if (clientCountry) { doc.text(clientCountry, left, ry); ry += 6; }

  // Town/date right above subject
  doc.setFontSize(11);
  doc.text(`${COMPANY.city}, ${formatLongDate(orderDate, tpl.locale)}`, right, 92, { align: 'right' });

  // Subject red bar
  doc.setFillColor(204, 0, 0);
  doc.rect(0, 98, pageWidth, 5, 'F');
  doc.setTextColor(255);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text(`${tpl.subject} ${invoiceNum}`, left, 101.8);
  doc.setTextColor(0);
  doc.setFont(undefined, 'normal');

  // Greeting and intro
  let by = 112;
  doc.setFontSize(10.5);
  doc.text(tpl.greeting, left + 4, by); by += 7;
  doc.text(tpl.introA, left + 4, by, { maxWidth: right - left - 4 }); by += 10;
  doc.text(tpl.introB, left + 4, by, { maxWidth: right - left - 4 }); by += 8;

  // Invoice table
  const rows = [
    { label: tpl.product, value: productName },
    { label: tpl.date, value: formatDateTime(orderDate, tpl.locale) },
    { label: tpl.price, value: fmtAmount(netAmount, currency) },
    { label: tpl.count, value: '1' },
    { label: tpl.net, value: fmtAmount(netAmount, currency) },
    { label: tpl.vat, value: fmtAmount(vatAmount, currency) },
    { label: tpl.total, value: fmtAmount(totalIncVat, currency) },
  ];
  by = drawKeyValueTable(doc, rows, by);

  // English invoices include a second CHF pricing table block.
  if (language === 'en') {
    doc.setFontSize(10);
    doc.text('By the swiss official Monthly middle course of 1.0000 CHF/CHF the pricing is in CHF:', left + 4, by, {
      maxWidth: right - left - 4,
    });
    by += 8;

    const chfRows = [
      { label: tpl.product, value: productName },
      { label: tpl.date, value: formatDateTime(orderDate, tpl.locale) },
      { label: tpl.price, value: fmtAmount(netAmount, 'CHF') },
      { label: tpl.count, value: '1' },
      { label: tpl.net, value: fmtAmount(netAmount, 'CHF') },
      { label: tpl.vat, value: fmtAmount(vatAmount, 'CHF') },
      { label: tpl.total, value: fmtAmount(totalIncVat, 'CHF') },
    ];
    by = drawKeyValueTable(doc, chfRows, by);
  }

  // Payment terms
  const deadlineDate = new Date(parseDate(orderDate));
  deadlineDate.setUTCDate(deadlineDate.getUTCDate() + 30);
  const deadline = formatLongDate(deadlineDate, tpl.locale);
  doc.setFontSize(10);
  doc.text(tpl.terms(deadline), left + 4, by, { maxWidth: right - left - 4 });
  by += 16;

  // Closing + signature image
  doc.text(tpl.closing, left + 4, by);
  by += 4;
  const signatureDataUrl = await loadImageAsDataURL(signatureImage);
  if (signatureDataUrl) {
    // Keep signature above footer area when invoice content is long.
    const signatureY = Math.min(by, 258);
    doc.addImage(signatureDataUrl, 'JPEG', left + 4, signatureY, 42, 15);
  }

  // Footer bank details
  if (currency === 'CHF') {
    const footerY = 276;
    const footerH = 21;
    drawCenteredFooter(
      doc,
      pageWidth,
      footerY,
      footerH,
      'Bankverbindungen:',
      [
        `CHF-GKB: ${COMPANY.bankCHF.holder}, IBAN: ${COMPANY.bankCHF.iban}, BIC: ${COMPANY.bankCHF.bic}`,
        `EUR-Wise: ${COMPANY.bankEUR.holder}, IBAN: ${COMPANY.bankEUR.iban}, BIC: ${COMPANY.bankEUR.bic}`,
        'Multi-Currency-Wise: https://wise.com/pay/business/financialmarketnavigatorag Wise-Tag: @financialmarketnavigatorag',
      ],
      7
    );
    const wiseY = footerY + 16;
    const urlText = 'https://wise.com/pay/business/financialmarketnavigatorag';
    const urlW = doc.getTextWidth(urlText);
    const prefix = 'Multi-Currency-Wise: ';
    const prefixW = doc.getTextWidth(prefix);
    const allLines = [
      'Bankverbindungen:',
      `CHF-GKB: ${COMPANY.bankCHF.holder}, IBAN: ${COMPANY.bankCHF.iban}, BIC: ${COMPANY.bankCHF.bic}`,
      `EUR-Wise: ${COMPANY.bankEUR.holder}, IBAN: ${COMPANY.bankEUR.iban}, BIC: ${COMPANY.bankEUR.bic}`,
      'Multi-Currency-Wise: https://wise.com/pay/business/financialmarketnavigatorag Wise-Tag: @financialmarketnavigatorag',
    ];
    const maxLineWidth = allLines.reduce((max, line) => {
      const w = doc.getTextWidth(line);
      return w > max ? w : max;
    }, 0);
    const startX = (pageWidth - maxLineWidth) / 2;
    const urlX = startX + prefixW;
    doc.setDrawColor(255);
    doc.setLineWidth(0.2);
    doc.line(urlX, wiseY + 0.6, urlX + urlW, wiseY + 0.6);
    doc.link(urlX, wiseY - 2.6, urlW, 3.2, { url: urlText });
  } else {
    const footerY = 276;
    const footerH = 21;
    drawCenteredFooter(
      doc,
      pageWidth,
      footerY,
      footerH,
      'Account Details:',
      [
      `CHF-GKB: ${COMPANY.bankCHF.holder}, IBAN: ${COMPANY.bankCHF.iban}, BIC: ${COMPANY.bankCHF.bic}`,
      `EUR-Wise: ${COMPANY.bankEUR.holder}, IBAN: ${COMPANY.bankEUR.iban}, BIC: ${COMPANY.bankEUR.bic}`,
        'Multi-Currency-Wise: https://wise.com/pay/business/financialmarketnavigatorag Wise-Tag: @financialmarketnavigatorag',
      ],
      7
    );
    const wiseY = footerY + 16;
    const urlText = 'https://wise.com/pay/business/financialmarketnavigatorag';
    const urlW = doc.getTextWidth(urlText);
    const prefix = 'Multi-Currency-Wise: ';
    const prefixW = doc.getTextWidth(prefix);
    const allLines = [
      'Account Details:',
      `CHF-GKB: ${COMPANY.bankCHF.holder}, IBAN: ${COMPANY.bankCHF.iban}, BIC: ${COMPANY.bankCHF.bic}`,
      `EUR-Wise: ${COMPANY.bankEUR.holder}, IBAN: ${COMPANY.bankEUR.iban}, BIC: ${COMPANY.bankEUR.bic}`,
      'Multi-Currency-Wise: https://wise.com/pay/business/financialmarketnavigatorag Wise-Tag: @financialmarketnavigatorag',
    ];
    const maxLineWidth = allLines.reduce((max, line) => {
      const w = doc.getTextWidth(line);
      return w > max ? w : max;
    }, 0);
    const startX = (pageWidth - maxLineWidth) / 2;
    const urlX = startX + prefixW;
    doc.setDrawColor(255);
    doc.setLineWidth(0.2);
    doc.line(urlX, wiseY + 0.6, urlX + urlW, wiseY + 0.6);
    doc.link(urlX, wiseY - 2.6, urlW, 3.2, { url: urlText });
  }
  doc.setTextColor(0);

  return doc;
}
